import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Building2, Calendar, Calculator, Shield, Plus, Search, Edit } from 'lucide-react';
import { payrollAPI } from '../../../services/api';
import { toast } from 'sonner';

const PayrollConfig = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('payroll-areas');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const tabs = [
    { id: 'payroll-areas', label: 'Payroll Areas', icon: Building2 },
    { id: 'payroll-periods', label: 'Payroll Periods', icon: Calendar },
    { id: 'tax-configs', label: 'Tax Configuration', icon: Calculator },
    { id: 'ss-configs', label: 'Social Security', icon: Shield }
  ];


  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      switch (activeTab) {
        case 'payroll-areas':
          response = await payrollAPI.getPayrollAreas();
          break;
        case 'payroll-periods':
          response = await payrollAPI.getPayrollPeriods();
          break;
        case 'tax-configs':
          response = await payrollAPI.getTaxConfigs();
          break;
        case 'ss-configs':
          response = await payrollAPI.getSSConfigs();
          break;
        default:
          response = { data: [] };
      }
      setData(response.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getIdField = () => {
    switch (activeTab) {
      case 'payroll-areas': return 'payroll_area_id';
      case 'payroll-periods': return 'id';
      case 'tax-configs': return 'tax_config_id';
      case 'ss-configs': return 'ss_config_id';
      default: return 'id';
    }
  };

  const filteredData = data.filter(item => {
    const name = item.name || '';
    const id = item[getIdField()] || '';
    return name.toLowerCase().includes(searchTerm.toLowerCase()) || 
           id.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const renderTable = () => {
    switch (activeTab) {
      case 'payroll-areas':
        return (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">ID</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Company</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Frequency</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Currency</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Employees</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredData.map((item) => (
                <tr key={item.payroll_area_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.payroll_area_id}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.company_code_name || item.company_code_id}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.frequency}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.currency}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.employee_count || 0}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="px-4 py-3">
                    <Button size="sm" variant="ghost" onClick={() => { setEditingItem(item); setShowForm(true); }}>
                      <Edit className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      case 'payroll-periods':
        return (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Payroll Area</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Year</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Period</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Start</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">End</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Payday</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredData.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-700">{item.payroll_area_name || item.payroll_area_id}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.period_year}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.period_number}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.period_start}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.period_end}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.payday}</td>
                  <td className="px-4 py-3">
                    <PeriodStatusBadge status={item.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      case 'tax-configs':
        return (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">ID</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Fiscal Year</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Slabs</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Cess Rate</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredData.map((item) => (
                <tr key={item.tax_config_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.tax_config_id}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.tax_type}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.fiscal_year}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.slab_count || item.tax_slabs?.length || 0}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.cess_rate}%</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="px-4 py-3">
                    <Button size="sm" variant="ghost" onClick={() => { setEditingItem(item); setShowForm(true); }}>
                      <Edit className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      case 'ss-configs':
        return (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">ID</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Employee %</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Employer %</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Ceiling</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredData.map((item) => (
                <tr key={item.ss_config_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.ss_config_id}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.ss_type}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.employee_contribution_rate}%</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.employer_contribution_rate}%</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.wage_ceiling ? `$${item.wage_ceiling}` : '-'}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="px-4 py-3">
                    <Button size="sm" variant="ghost" onClick={() => { setEditingItem(item); setShowForm(true); }}>
                      <Edit className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      default:
        return null;
    }
  };

  const handleGeneratePeriods = async () => {
    const areaId = prompt('Enter Payroll Area ID:');
    if (!areaId) return;
    
    const year = parseInt(prompt('Enter Year:', new Date().getFullYear().toString()) || '0');
    if (!year) return;

    try {
      await payrollAPI.generatePeriods(areaId, year, year);
      toast.success('Periods generated successfully');
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to generate periods');
      }
    }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="payroll-config-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm')}>HCM</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm/payroll')}>Payroll</span>
            <span>/</span>
            <span>Configuration</span>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Payroll Configuration</h1>
              <p className="text-gray-600 mt-1">Configure payroll areas, periods, taxes, and social security</p>
            </div>
            <div className="flex gap-2">
              {activeTab === 'payroll-periods' && (
                <Button variant="outline" onClick={handleGeneratePeriods}>
                  <Calendar className="h-4 w-4 mr-2" />
                  Generate Periods
                </Button>
              )}
              {activeTab !== 'payroll-periods' && (
                <Button onClick={() => { setEditingItem(null); setShowForm(true); }} data-testid="btn-create">
                  <Plus className="h-4 w-4 mr-2" />
                  Create New
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id 
                  ? 'bg-orange-100 text-orange-700' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="flex gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Table */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : filteredData.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No data found. Click "Create New" to add a record.
            </div>
          ) : (
            <div className="overflow-x-auto">
              {renderTable()}
            </div>
          )}
        </Card>

        {showForm && (
          <ConfigFormModal 
            activeTab={activeTab}
            editingItem={editingItem}
            onClose={() => { setShowForm(false); setEditingItem(null); }}
            onSave={() => { setShowForm(false); setEditingItem(null); loadData(); }}
          />
        )}
      </div>
    </Layout>
  );
};

// Helper Components
const StatusBadge = ({ status }) => {
  const colors = {
    'Active': 'bg-green-100 text-green-700',
    'Blocked': 'bg-red-100 text-red-700'
  };
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
};

const PeriodStatusBadge = ({ status }) => {
  const colors = {
    'Open': 'bg-green-100 text-green-700',
    'In_Progress': 'bg-yellow-100 text-yellow-700',
    'Closed': 'bg-gray-100 text-gray-700'
  };
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
      {status?.replace('_', ' ')}
    </span>
  );
};

// Config Form Modal (with full implementation)
const ConfigFormModal = ({ activeTab, editingItem, onClose, onSave }) => {
  const [formData, setFormData] = useState(editingItem || {});
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let updateData = {};
      
      switch (activeTab) {
        case 'payroll-areas':
          updateData = {
            payroll_area_id: formData.payroll_area_id,
            name: formData.name,
            description: formData.description,
            company_code_id: formData.company_code_id,
            frequency: formData.frequency || 'Monthly',
            currency: formData.currency || 'USD',
            status: formData.status || 'Active'
          };
          if (editingItem) {
            await payrollAPI.updatePayrollArea(editingItem.payroll_area_id, updateData);
          } else {
            await payrollAPI.createPayrollArea(updateData);
          }
          break;
          
        case 'tax-configs':
          updateData = {
            tax_config_id: formData.tax_config_id,
            name: formData.name,
            description: formData.description,
            tax_type: formData.tax_type || 'INCOME_TAX',
            fiscal_year: formData.fiscal_year,
            cess_rate: parseFloat(formData.cess_rate) || 0,
            surcharge_rate: parseFloat(formData.surcharge_rate) || 0,
            status: formData.status || 'Active'
          };
          if (editingItem) {
            await payrollAPI.updateTaxConfig(editingItem.tax_config_id, updateData);
          } else {
            await payrollAPI.createTaxConfig(updateData);
          }
          break;
          
        case 'ss-configs':
          updateData = {
            ss_config_id: formData.ss_config_id,
            name: formData.name,
            description: formData.description,
            ss_type: formData.ss_type || 'SOCIAL_SECURITY',
            employee_contribution_rate: parseFloat(formData.employee_contribution_rate) || 0,
            employer_contribution_rate: parseFloat(formData.employer_contribution_rate) || 0,
            wage_ceiling: formData.wage_ceiling ? parseFloat(formData.wage_ceiling) : null,
            wage_floor: formData.wage_floor ? parseFloat(formData.wage_floor) : null,
            status: formData.status || 'Active'
          };
          if (editingItem) {
            await payrollAPI.updateSSConfig(editingItem.ss_config_id, updateData);
          } else {
            await payrollAPI.createSSConfig(updateData);
          }
          break;
          
        default:
          toast.error('Form not implemented for this type');
          setLoading(false);
          return;
      }
      
      toast.success(editingItem ? 'Updated successfully' : 'Created successfully');
      onSave();
    } catch (error) {
      console.error('Operation failed:', error);
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Operation failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderFormFields = () => {
    switch (activeTab) {
      case 'payroll-areas':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payroll Area ID *</label>
                <Input
                  value={formData.payroll_area_id || ''}
                  onChange={(e) => setFormData({...formData, payroll_area_id: e.target.value.toUpperCase()})}
                  disabled={!!editingItem}
                  placeholder="e.g., PA01"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <Input
                  value={formData.name || ''}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Monthly Payroll"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Input
                value={formData.description || ''}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Code</label>
                <Input
                  value={formData.company_code_id || ''}
                  onChange={(e) => setFormData({...formData, company_code_id: e.target.value})}
                  placeholder="e.g., CC001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Frequency *</label>
                <select
                  value={formData.frequency || 'Monthly'}
                  onChange={(e) => setFormData({...formData, frequency: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Monthly">Monthly</option>
                  <option value="Bi-Weekly">Bi-Weekly</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Semi-Monthly">Semi-Monthly</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select
                  value={formData.currency || 'USD'}
                  onChange={(e) => setFormData({...formData, currency: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="INR">INR - Indian Rupee</option>
                  <option value="JPY">JPY - Japanese Yen</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status || 'Active'}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Active">Active</option>
                  <option value="Blocked">Blocked</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 'tax-configs':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tax Config ID *</label>
                <Input
                  value={formData.tax_config_id || ''}
                  onChange={(e) => setFormData({...formData, tax_config_id: e.target.value.toUpperCase()})}
                  disabled={!!editingItem}
                  placeholder="e.g., TAX_2025"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <Input
                  value={formData.name || ''}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Income Tax FY 2025"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Input
                value={formData.description || ''}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tax Type *</label>
                <select
                  value={formData.tax_type || 'INCOME_TAX'}
                  onChange={(e) => setFormData({...formData, tax_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="INCOME_TAX">Income Tax</option>
                  <option value="PAYROLL_TAX">Payroll Tax</option>
                  <option value="PROFESSIONAL_TAX">Professional Tax</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fiscal Year *</label>
                <Input
                  value={formData.fiscal_year || ''}
                  onChange={(e) => setFormData({...formData, fiscal_year: e.target.value})}
                  placeholder="e.g., 2024-2025"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cess Rate (%)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.cess_rate || 0}
                  onChange={(e) => setFormData({...formData, cess_rate: e.target.value})}
                  min={0}
                  max={100}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Surcharge Rate (%)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.surcharge_rate || 0}
                  onChange={(e) => setFormData({...formData, surcharge_rate: e.target.value})}
                  min={0}
                  max={100}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status || 'Active'}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Active">Active</option>
                  <option value="Blocked">Blocked</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 'ss-configs':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SS Config ID *</label>
                <Input
                  value={formData.ss_config_id || ''}
                  onChange={(e) => setFormData({...formData, ss_config_id: e.target.value.toUpperCase()})}
                  disabled={!!editingItem}
                  placeholder="e.g., SS_2025"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <Input
                  value={formData.name || ''}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Social Security 2025"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Input
                value={formData.description || ''}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SS Type *</label>
                <select
                  value={formData.ss_type || 'SOCIAL_SECURITY'}
                  onChange={(e) => setFormData({...formData, ss_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="SOCIAL_SECURITY">Social Security</option>
                  <option value="MEDICARE">Medicare</option>
                  <option value="PENSION">Pension Fund</option>
                  <option value="PROVIDENT_FUND">Provident Fund</option>
                  <option value="HEALTH_INSURANCE">Health Insurance</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status || 'Active'}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Active">Active</option>
                  <option value="Blocked">Blocked</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee Contribution (%)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.employee_contribution_rate || 0}
                  onChange={(e) => setFormData({...formData, employee_contribution_rate: e.target.value})}
                  min={0}
                  max={100}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employer Contribution (%)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.employer_contribution_rate || 0}
                  onChange={(e) => setFormData({...formData, employer_contribution_rate: e.target.value})}
                  min={0}
                  max={100}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Wage Ceiling</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.wage_ceiling || ''}
                  onChange={(e) => setFormData({...formData, wage_ceiling: e.target.value})}
                  placeholder="Max wage for contributions"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty for no ceiling</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Wage Floor</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.wage_floor || ''}
                  onChange={(e) => setFormData({...formData, wage_floor: e.target.value})}
                  placeholder="Min wage for contributions"
                />
              </div>
            </div>
          </div>
        );

      default:
        return (
          <p className="text-gray-500">
            Edit form for {activeTab} is not yet implemented.
          </p>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">
            {editingItem ? 'Edit' : 'Create'} {activeTab.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6">
          {renderFormFields()}
          <div className="flex justify-end gap-3 pt-6 border-t mt-6">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PayrollConfig;
